import { createTheme } from '@mui/material/styles';

const font = "'Lato'";

const primary1 = '#00b398'; // dark green
const primary2 = '#00313c'; // black
const primary3 = '#d9d9d6'; // gray
const secondary1 = '#25bf6e'; // medium green
const secondary2 = '#82de5b'; // light green
const secondary3 = '#82de5b'; // lighter green
const tealText = '#009881'; // teal
const chipBg = 'rgba(0, 49, 60, 0.09)'; // light gray
const outlineInactive = 'rgba(0, 49, 60, 0.6)'; // dark gray
const error = 'rgba(255, 0, 0, 0.6)'; // red
const white = '#fff'; // white

const theme = createTheme({
  typography: {
    fontFamily: font,
    bannerHead: {
      fontSize: 44,
      fontWeight: 'bold',
      lineHeight: 'normal',
      letterSpacing: 'normal',
      fontFamily: 'Lato',
    },
    researchBannerHeader: {
      fontSize: 26,
      fontWeight: 'bold',
      lineHeight: 'normal',
      letterSpacing: '-0.19px',
      color: primary2,
      fontFamily: 'Lato',
    },
    researchBannerHeaderForMobile: {
      fontSize: 18,
      fontWeight: 'bold',
      lineHeight: 'normal',
      letterSpacing: 'normal',
      color: primary2,
      fontFamily: 'Lato',
    },
    bannerBody: {
      fontSize: 18,
      fontWeight: 'normal',
      lineHeight: 'normal',
      letterSpacing: '0.6px',
      fontFamily: 'Lato',
    },
    cardBody: {
      fontSize: 14,
      fontWeight: 'normal',
      lineHeight: 'normal',
      letterSpacing: 'normal',
      color: primary2,
      fontFamily: 'Lato',
    },
    cardBody1: {
      fontSize: 14,
      fontWeight: 'normal',
      lineHeight: '1.36',
      letterSpacing: 'normal',
      color: primary2,
    },
    menuLink: {
      fontSize: 15,
      fontWeight: 600,
      lineHeight: 'normal',
      letterSpacing: 0.5,
      color: white,
      fontFamily: 'Lato',
    },
    body1: {
      fontSize: 15,
      fontWeight: 'normal',
      lineHeight: 'normal',
      letterSpacing: 0.5,
      color: primary2,
    },
    h1: {
      fontSize: 96,
      fontWeight: 300,
      letterSpacing: -1.5,
      color: primary2,
    },
    h2: {
      fontSize: 60,
      fontWeight: 300,
      letterSpacing: -0.5,
      color: primary2,
    },
    h3: {
      fontSize: 48,
      fontWeight: 400,
      color: primary2,
    },
    h4: {
      fontSize: 34,
      fontWeight: 400,
      letterSpacing: -0.25,
      color: primary2,
    },
    h4Bold: {
      fontSize: 32,
      fontWeight: 'bold',
      color: primary2,
      lineHeight: 'normal',
      letterSpacing: 'normal',
      fontFamily: 'Lato',
    },
    h5: {
      fontSize: 24,
      fontWeight: 400,
      color: primary2,
      lineHeight: 'normal',
      letterSpacing: 'normal',
    },
    h5Bold: {
      fontSize: 24,
      fontWeight: 'bold',
      color: primary2,
      lineHeight: 'normal',
      letterSpacing: 'normal',
      fontFamily: 'Lato',
    },
    h6: {
      fontSize: 20,
      fontWeight: 500,
      letterSpacing: 0.16,
      lineHeight: 'normal',
      color: primary2,
    },
    h6Small: {
      fontSize: 18,
      fontWeight: 500,
      letterSpacing: 'normal',
      lineHeight: 'normal',
      color: primary2,
      fontFamily: 'Lato',
    },

    tableHead: {
      fontSize: 15,
      fontWeight: 600,
      letterSpacing: 0.5,
      color: primary2,
    },
    subtitle1: {
      fontSize: 14,
      fontWeight: 600,
      letterSpacing: 0.15,
      color: primary2,
    },
    subtitle2: {
      fontSize: 13,
      fontWeight: 600,
      letterSpacing: 0.1,
      lineHeight: 'normal',
      color: primary2,
    },
    button: {
      fontSize: 14,
      fontWeight: 600,
      letterSpacing: 1.25,
      textAlign: 'center',
      lineHeight: 'normal',
    },
    link: {
      fontSize: 14,
      fontWeight: 400,
      letterSpacing: 0.27,
      color: tealText,
    },
    body2: {
      fontSize: 13,
      fontWeight: 400,
      letterSpacing: 0.2,
      lineHeight: 1.38,
      color: primary2,
    },
    caption: {
      fontSize: 12,
      fontWeight: 400,
      letterSpacing: 0.4,
      color: primary2,
      minHeight: 12,
      lineHeight: 'normal',
    },
    captionBold: {
      fontSize: 12,
      fontWeight: 'bold',
      letterSpacing: 0.4,
      color: primary2,
    },
    captionGrey: {
      fontSize: 12,
      fontWeight: 400,
      letterSpacing: 0.4,
      color: outlineInactive,
    },
    captionLink: {
      fontSize: 12,
      fontWeight: 400,
      letterSpacing: 0.4,
      color: tealText,
    },
    overline1: {
      fontSize: 10,
      fontWeight: 400,
      letterSpacing: 'normal',
      color: primary2,
    },
    overline2: {
      fontSize: 10,
      fontWeight: 400,
      letterSpacing: 'normal',
      color: outlineInactive,
    },
    overlineLink: {
      fontSize: 10,
      fontWeight: 400,
      letterSpacing: 1,
      color: tealText,
    },
    fieldError: {
      fontSize: 12,
      fontWeight: 400,
      letterSpacing: 0.4,
      color: error,
    },
    tealText: {
      fontSize: 12,
      fontWeight: 400,
      letterSpacing: 0.4,
      color: tealText,
    },
    verySmallText: {
      fontSize: 8,
      fontWeight: 'normal',
      lineHeight: 'normal',
      letterSpacing: 'normal',
      color: outlineInactive,
    },
  },
  palette: {
    primary: {
      main: primary1,
      contrastText: white,
    },
    primary1: {
      main: primary1,
      contrastText: white,
    },
    primary2: {
      main: primary2,
      contrastText: white,
    },
    primary3: {
      main: primary3,
      contrastText: white,
    },
    secondary1: {
      main: secondary1,
    },
    secondary2: {
      main: secondary2,
    },
    secondary3: {
      main: secondary3,
    },
    tealText: {
      main: tealText,
    },
    chipBg: {
      main: chipBg,
    },
    outlineInactive: {
      main: outlineInactive,
    },
    error: {
      main: error,
    },
    white: {
      main: white,
    },
  },
  spacing: 4,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1024,
      lg: 1200,
      xl: 1536,
    },
  },
  components: {
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: '#30343F !important',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: '#30343F !important',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          marginTop: '0',
          marginBottom: '5px',
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          fontSize: '13px',
          lineHeight: '1.38',
          letterSpacing: '0.2px',
        },
        input: {
          paddingTop: '9px',
          paddingBottom: '4px',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          height: '15px',
          letterSpacing: '0.4px',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '10px',
          lineHeight: 'normal',
          height: '12px',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          fontFamily: font,
        },
      },
    },
  },
});

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    primary1: true;
    primary2: true;
    primary3: true;
    tealText: true;
    chipBg: true;
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    bannerHead: React.CSSProperties;
    bannerBody: React.CSSProperties;
    cardBody: React.CSSProperties;
    cardBody1: React.CSSProperties;
    menuLink: React.CSSProperties;
    captionGrey: React.CSSProperties;
    h4Bold: React.CSSProperties;
    h5Bold: React.CSSProperties;
    h6Small: React.CSSProperties;
    tableHead: React.CSSProperties;
    link: React.CSSProperties;
    captionBold: React.CSSProperties;
    captionLink: React.CSSProperties;
    overline1: React.CSSProperties;
    overline2: React.CSSProperties;
    overlineLink: React.CSSProperties;
    fieldError: React.CSSProperties;
    tealText: React.CSSProperties;
    verySmallText: React.CSSProperties;
    researchBannerHeader: React.CSSProperties;
    researchBannerHeaderForMobile: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    bannerHead?: React.CSSProperties;
    bannerBody?: React.CSSProperties;
    cardBody?: React.CSSProperties;
    cardBody1?: React.CSSProperties;
    menuLink?: React.CSSProperties;
    captionGrey?: React.CSSProperties;
    h4Bold?: React.CSSProperties;
    h5Bold?: React.CSSProperties;
    h6Small?: React.CSSProperties;
    tableHead?: React.CSSProperties;
    link?: React.CSSProperties;
    captionBold?: React.CSSProperties;
    captionLink?: React.CSSProperties;
    overline1?: React.CSSProperties;
    overline2?: React.CSSProperties;
    overlineLink?: React.CSSProperties;
    fieldError?: React.CSSProperties;
    tealText?: React.CSSProperties;
    verySmallText?: React.CSSProperties;
    researchBannerHeader?: React.CSSProperties;
    researchBannerHeaderForMobile?: React.CSSProperties;
  }

  interface Palette {
    primary1: Palette['primary'];
    primary2: Palette['primary'];
    primary3: Palette['primary'];
    secondary1: Palette['primary'];
    secondary2: Palette['primary'];
    secondary3: Palette['primary'];
    tealText: Palette['primary'];
    chipBg: Palette['primary'];
    outlineInactive: Palette['primary'];
    white: Palette['primary'];
  }
  interface PaletteOptions {
    primary1: PaletteOptions['primary'];
    primary2: PaletteOptions['primary'];
    primary3: PaletteOptions['primary'];
    secondary1: PaletteOptions['primary'];
    secondary2: PaletteOptions['primary'];
    secondary3: PaletteOptions['primary'];
    tealText: PaletteOptions['primary'];
    chipBg: PaletteOptions['primary'];
    outlineInactive: PaletteOptions['primary'];
    white: PaletteOptions['primary'];
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    bannerHead: true;
    bannerBody: true;
    cardBody: true;
    cardBody1: true;
    menuLink: true;
    captionGrey: true;
    tableHead: true;
    link: true;
    captionBold: true;
    captionLink: true;
    h4Bold: true;
    h5Bold: true;
    h6Small: true;
    overline1: true;
    overline2: true;
    overlineLink: true;
    fieldError: true;
    caption: true;
    tealText: true;
    verySmallText: true;
    researchBannerHeader: true;
    researchBannerHeaderForMobile: true;
  }
}

export default theme;
